import { Tooltip } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { ChordCaptureEditor } from 'app/modules/wallforming/components/input-binding/ChordCaptureEditor';
import { ModifierBindingEditor } from 'app/modules/wallforming/components/input-binding/ModifierBindingEditor';
import {
    INPUT_BINDING_SLOTS,
    inputActions
} from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { ReservedInput } from 'app/modules/wallforming/components/input-binding/types/reservedInput';
import { TranslationKey } from 'app/modules/wallforming/components/input-binding/types/translationKey';

export const BINDING_GRID_COLUMNS = `minmax(0, 1fr) repeat(${INPUT_BINDING_SLOTS}, 13rem)`;

interface BindingLabelProps {
    labelKey: TranslationKey;
    isLocked?: boolean;
}

export const BindingLabel = ({ labelKey, isLocked }: BindingLabelProps) => {
    const { t } = useTranslation();
    return (
        <div className="flex min-w-0 items-center gap-1.5">
            <span className="whitespace-normal wrap-break-word text-xs leading-tight">{t(labelKey)}</span>
            {isLocked && (
                <Tooltip label={t('labelKeyBindingLockedTooltip')} zIndex={20000}>
                    <span className="flex">
                        <Icon className="icon-sm text-text-placeholder" icon="lock" />
                    </span>
                </Tooltip>
            )}
        </div>
    );
};

/** Renders a row purely from the action metadata; the editor strategy follows the devices the action accepts. */
export const InputBindingRow = memo(function InputBindingRow({ actionId }: { actionId: InputActionId }) {
    const { labelKey, slots } = inputActions[actionId];

    return (
        <div
            className="grid items-center gap-x-2 rounded-md px-4 py-1 hover:bg-background-gray-ultra-light"
            style={{ gridTemplateColumns: BINDING_GRID_COLUMNS }}
        >
            <BindingLabel labelKey={labelKey} />
            {Array.from({ length: slots }, (_, slot) => (
                <div
                    key={slot}
                    className="min-w-0"
                    style={slot === slots - 1 ? { gridColumn: `span ${INPUT_BINDING_SLOTS - slot}` } : undefined}
                >
                    {inputActions[actionId].keyboardModifierOnly ? (
                        <ModifierBindingEditor actionId={actionId} slot={slot} />
                    ) : (
                        <ChordCaptureEditor actionId={actionId} slot={slot} />
                    )}
                </div>
            ))}
        </div>
    );
});

export const ReservedInputRow = ({ input }: { input: ReservedInput }) => (
    <div
        className="grid items-center gap-x-2 rounded-md px-4 py-1"
        style={{ gridTemplateColumns: BINDING_GRID_COLUMNS }}
        aria-disabled
    >
        <BindingLabel labelKey={input.labelKey} isLocked />
        <div className="flex h-7 w-28 items-center justify-center rounded border border-border-ultra-light text-xs text-text-placeholder">
            {input.displayLabel}
        </div>
    </div>
);
